import { type ITourConfig, ETourId } from './types';
import {
  draftWelcomeTour,
  draftEditorTour,
  draftAutomationEditViewTour,
  inputsTriggersTour,
} from './tours/draft-creation.tour';
import { exceptionHighlightTour } from './tours/exception-resolution.tour';
import { successfulDraftRunTour } from './tours/successful-draft-run.tour';
import { viewingPublishedAutomationTour } from './tours/viewing-published-automation.tour';
import { monitorIntroTour } from './tours/monitor-intro.tour';
import { automationListDashboardTour } from './tours/automation-list-dashboard.tour';
import { guidanceCenterTour } from './tours/guidance-center.tour';
import { guidanceCenterActionItemTour } from './tours/guidance-center-action-item-view.tour';
import { guidanceCenterActionItemReviewTour } from './tours/guidance-center-action-item-review.tour';
import { integrationsListTour } from './tours/integrations-list.tour';
import { performanceDashboardTour } from './tours/performance-dashboard.tour';
import { workspaceSettingsTour } from './tours/workspace-settings.tour';

/**
 * Central registry of all tours in the application.
 *
 * Add new tour configurations here. Each tour is keyed by its ETourId
 * so it can be looked up efficiently throughout the app.
 */
const TOUR_REGISTRY: Record<ETourId, ITourConfig> = {
  [ETourId.DRAFT_WELCOME]: draftWelcomeTour,
  [ETourId.DRAFT_EDITOR]: draftEditorTour,
  [ETourId.DRAFT_AUTOMATION_EDIT_VIEW]: draftAutomationEditViewTour,
  [ETourId.INPUTS_TRIGGERS]: inputsTriggersTour,
  [ETourId.EXCEPTION_HIGHLIGHT]: exceptionHighlightTour,
  [ETourId.SUCCESSFUL_DRAFT_RUN]: successfulDraftRunTour,
  [ETourId.VIEWING_PUBLISHED_AUTOMATION]: viewingPublishedAutomationTour,
  [ETourId.MONITOR_INTRO]: monitorIntroTour,
  [ETourId.AUTOMATION_LIST_DASHBOARD]: automationListDashboardTour,
  [ETourId.GUIDANCE_CENTER]: guidanceCenterTour,
  [ETourId.GUIDANCE_CENTER_ACTION_ITEM]: guidanceCenterActionItemTour,
  [ETourId.GUIDANCE_CENTER_ACTION_ITEM_REVIEW]:
    guidanceCenterActionItemReviewTour,
  [ETourId.INTEGRATIONS_LIST]: integrationsListTour,
  [ETourId.PERFORMANCE_DASHBOARD]: performanceDashboardTour,
  [ETourId.WORKSPACE_SETTINGS]: workspaceSettingsTour,
};

/**
 * Retrieves a tour configuration by its ID.
 *
 * @param tourId - The unique tour identifier
 * @returns The tour configuration
 * @throws Error if the tour ID is not registered
 */
export function getTourConfig(tourId: ETourId): ITourConfig {
  const config = TOUR_REGISTRY[tourId];
  if (!config) {
    throw new Error(
      `Tour "${tourId}" is not registered. Add it to TOUR_REGISTRY in registry.ts.`,
    );
  }
  return config;
}

/**
 * Returns all registered tour configurations.
 *
 * @returns Array of all tour configs
 */
export function getAllTourConfigs(): ITourConfig[] {
  return Object.values(TOUR_REGISTRY);
}

/**
 * Checks if a tour ID is registered.
 *
 * @param tourId - The tour ID to check
 * @returns True if the tour is registered
 */
export function isTourRegistered(tourId: ETourId): boolean {
  return tourId in TOUR_REGISTRY;
}
